import React, { useMemo } from 'react';
import { Search as SearchIcon, Globe, Tag, Clock, User, Filter as FilterIcon, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SegmentedControl } from '@/components/voice/SegmentedControl';
import { ActiveFilterChips } from '@/components/voice/ActiveFilterChips';
import { useDebouncedValue } from '@/hooks/ui/useDebouncedValue';

type ViewMode = 'list' | 'compact' | 'detailed';
type VoiceTab = 'recommended' | 'favorites' | 'all';

interface VoiceFiltersProps {
  value: {
    search: string;
    category: string;
    accent: string;
    age: string;
    gender: string;
    language: string;
    viewMode: ViewMode;
    tab: VoiceTab;
  };
  onChange: (next: Partial<VoiceFiltersProps['value']>) => void;
  onReset: () => void;
  categories: string[];
  options: { accents: string[]; ages: string[]; genders: string[]; languages: string[] };
  counts?: { favorites?: number };
  isLoadingAll?: boolean;
  resultsLabel?: string;
}

export function VoiceFilters({ value, onChange, onReset, categories, options, counts, isLoadingAll, resultsLabel }: VoiceFiltersProps) {
  const debouncedSearch = useDebouncedValue(value.search, 250);

  // propagate debounced search to parent when it changes
  React.useEffect(() => {
    if (debouncedSearch !== value.search) {
      onChange({ search: debouncedSearch });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const activeChips = useMemo(() => {
    const chips = [] as Array<{ key: string; label: string }>;
    if (value.category !== 'all') chips.push({ key: 'category', label: `Category: ${value.category}` });
    if (value.accent !== 'all') chips.push({ key: 'accent', label: `Accent: ${value.accent}` });
    if (value.age !== 'all') chips.push({ key: 'age', label: `Age: ${value.age}` });
    if (value.gender !== 'all') chips.push({ key: 'gender', label: `Gender: ${value.gender}` });
    if (value.language !== 'all') chips.push({ key: 'language', label: `Language: ${value.language}` });
    return chips;
  }, [value]);

  return (
    <div className="sticky top-0 z-10 -mx-4 px-4 pt-2 pb-3 bg-white/90 backdrop-blur border-b border-gray-200">
      {/* Tabs & View mode row */}
      <div className="flex items-center justify-between mb-3">
        <Tabs value={value.tab} onValueChange={(v) => onChange({ tab: v as VoiceTab })}>
          <TabsList className="grid grid-cols-3 bg-gray-100/50 p-1 rounded-lg">
            <TabsTrigger
              value="recommended"
              className="data-[state=active]:bg-genie-primary data-[state=active]:text-white data-[state=active]:shadow-md transition-all"
            >
              Preselected
            </TabsTrigger>
            <TabsTrigger
              value="favorites"
              className="data-[state=active]:bg-genie-primary data-[state=active]:text-white data-[state=active]:shadow-md transition-all"
            >
              <span className="flex items-center gap-1">
                Favorites
                {counts?.favorites ? (
                  <span className="px-1.5 py-0.5 text-xs bg-white/20 backdrop-blur rounded-full">{counts.favorites}</span>
                ) : null}
              </span>
            </TabsTrigger>
            <TabsTrigger
              value="all"
              className="data-[state=active]:bg-genie-primary data-[state=active]:text-white data-[state=active]:shadow-md transition-all flex items-center gap-1"
            >
              All Voices
              {isLoadingAll && (
                <Loader2 className="h-3 w-3 animate-spin" />
              )}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="flex items-center gap-3">
          <SegmentedControl
            value={value.viewMode}
            onChange={(v) => onChange({ viewMode: v })}
            options={[
              { value: 'list', label: 'List' },
              { value: 'compact', label: 'Compact' },
              { value: 'detailed', label: 'Detailed' },
            ]}
          />
          <Button variant="ghost" size="sm" className="text-sm" onClick={onReset}>
            Reset All
          </Button>
        </div>
      </div>

      {/* Filters row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
        {/* Search */}
        <div className="relative lg:col-span-1">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            value={value.search}
            onChange={(e) => onChange({ search: e.target.value })}
            placeholder="Search voices..."
            className="pl-10 h-9"
          />
        </div>

        {/* Category */}
        <Select value={value.category} onValueChange={(v) => onChange({ category: v })}>
          <SelectTrigger className="h-9 rounded-full border-gray-200">
            <div className="inline-flex items-center gap-2">
              <Tag className="h-4 w-4 text-gray-500" />
              <SelectValue placeholder="All Categories" />
            </div>
          </SelectTrigger>
          <SelectContent className="max-h-64">
            <SelectItem value="all">All Categories</SelectItem>
            {categories.map((c) => (
              <SelectItem key={c} value={c}>{c}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Accent */}
        <Select value={value.accent} onValueChange={(v) => onChange({ accent: v })}>
          <SelectTrigger className="h-9 rounded-full border-gray-2 00">
            <div className="inline-flex items-center gap-2">
              <Globe className="h-4 w-4 text-gray-500" />
              <SelectValue placeholder="All Accents" />
            </div>
          </SelectTrigger>
          <SelectContent className="max-h-64">
            <SelectItem value="all">All Accents</SelectItem>
            {options.accents.map((a) => (
              <SelectItem key={a} value={a}>{a}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Language */}
        <Select value={value.language} onValueChange={(v) => onChange({ language: v })}>
          <SelectTrigger className="h-9 rounded-full border-gray-200">
            <div className="inline-flex items-center gap-2">
              <Globe className="h-4 w-4 text-gray-500" />
              <SelectValue placeholder="All Languages" />
            </div>
          </SelectTrigger>
          <SelectContent className="max-h-64">
            <SelectItem value="all">All Languages</SelectItem>
            {options.languages.map((l) => (
              <SelectItem key={l} value={l}>{l}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Age */}
        <Select value={value.age} onValueChange={(v) => onChange({ age: v })}>
          <SelectTrigger className="h-9 rounded-full border-gray-200">
            <div className="inline-flex items-center gap-2">
              <Clock className="h-4 w-4 text-gray-500" />
              <SelectValue placeholder="All Ages" />
            </div>
          </SelectTrigger>
          <SelectContent className="max-h-64">
            <SelectItem value="all">All Ages</SelectItem>
            {options.ages.map((a) => (
              <SelectItem key={a} value={a}>{a}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Gender */}
        <Select value={value.gender} onValueChange={(v) => onChange({ gender: v })}>
          <SelectTrigger className="h-9 rounded-full border-gray-200">
            <div className="inline-flex items-center gap-2">
              <User className="h-4 w-4 text-gray-500" />
              <SelectValue placeholder="All Genders" />
            </div>
          </SelectTrigger>
          <SelectContent className="max-h-64">
            <SelectItem value="all">All Genders</SelectItem>
            {options.genders.map((g) => (
              <SelectItem key={g} value={g}>{g}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary and active chips */}
      <div className="mt-3 flex items-center justify-between text-sm text-gray-600">
        <div className="inline-flex items-center gap-2">
          <FilterIcon className="h-4 w-4 text-gray-500" />
          <span>{resultsLabel}</span>
        </div>
      </div>
      <ActiveFilterChips
        chips={activeChips}
        onRemove={(key) => onChange({ [key]: 'all' } as any)}
        onClearAll={onReset}
      />
    </div>
  );
}


